import { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../apiClient';

function formatDate(value) {
  if (!value) return '–';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('de-DE');
}

function formatNumber(value, digits = 2) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '–';
  return parsed.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function formatPercent(value, digits = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '–';
  return `${parsed.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
}

function formatCsvNumber(value, digits = 2) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '';
  return parsed.toLocaleString('de-DE', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: false,
  });
}

function toCsvCell(value) {
  const text = String(value ?? '');
  if (!/[;"\n\r]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

function toCsvRow(fields) {
  return fields.map(toCsvCell).join(';');
}

function csvFilenamePart(value) {
  return String(value || 'index')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'index';
}

const INDEX_INVEST_GUIDE_SECTIONS = [
  {
    title: '📚 Zusammenfassung von Index-Invest',
    paragraphs: [
      'Index-Invest ist ein regelbasiertes Steuerungssystem für langfristige ETF-Portfolios – ganz ohne Spekulation oder Entscheidungen aus dem Bauch heraus. Statt Krisen im Depot mit hohem vorübergehendem Drawdown auszusitzen, steigt Index-Invest aus dem Markt aus und wartet auf bessere Bedingungen, bevor wieder investiert wird.',
      'Die Signale können mit handelsüblichen ETFs auf S&P 500, Nasdaq 100, DAX oder MSCI World umgesetzt werden. Dafür kann der eigene Broker nach Wahl genutzt werden. Über die Jahre gab es nur wenige Signale, sodass das Regelwerk besonders für langfristige Anleger geeignet ist, die Risiko und Drawdown reduzieren möchten.',
    ],
  },
  {
    title: '⚙️ Umsetzung',
    bullets: [
      '🟢 Grün: In den Markt einsteigen bzw. investiert bleiben.',
      '🟡 Gelb: Kürzlicher Wechsel zwischen Grün und Rot.',
      '🔴 Rot: Markt verlassen, ETF-Positionen verkaufen und in Cash gehen, z. B. Tagesgeld oder Festgeld.',
      'Optional kann ein E-Mail-Alert aktiviert werden. Dann wird der Index-Invest-Status in die wöchentliche RSL-Benachrichtigung aufgenommen.',
    ],
  },
  {
    title: '🚀 Rendite-Booster',
    paragraphs: [
      'Index-Invest dient primär der Vermeidung größerer Drawdown-Phasen und weniger der reinen Rendite-Optimierung.',
      'Wer mit höheren Schwankungen gut leben kann, kann die Beimischung eines gehebelten Index-ETFs als aggressive Vergleichsvariante prüfen. Die Idee: Der Hebel verstärkt Aufwärtsphasen, während die Ampel versucht, lange schwache Marktphasen zu filtern.',
    ],
  },
  {
    title: '💡 Was sonst noch wichtig ist',
    paragraphs: [
      'Index-Invest berechnet Ein- und Ausstiegssignale aus dem vergangenen Kursverlauf des jeweiligen Index. Dadurch wird nie der perfekte Einstiegs- oder Ausstiegszeitpunkt getroffen. Ziel ist nicht perfektes Timing, sondern die Reduktion großer Verlustphasen.',
      'Cash dient dabei nicht als Renditeoptimierung, sondern als Risikopuffer. Nicht investiertes Kapital kann vorübergehend z. B. über Tagesgeld angelegt werden; verschiedene Zinssätze können im Modul simuliert werden.',
      'Der Verkauf von ETF-Positionen kann steuerlich relevant sein. Gewinne, auf die Steuern anfallen, stehen nicht mehr vollständig zur Reinvestition zur Verfügung. Für steuerliche Fragen sollte qualifizierte Beratung eingeholt werden.',
    ],
  },
];

function statusMeta(status, trend, COLORS) {
  if (status === 'green') {
    return { label: 'Grün', color: COLORS.green, bg: `${COLORS.green}22`, border: `${COLORS.green}55` };
  }
  if (status === 'red') {
    return { label: 'Rot', color: COLORS.red, bg: `${COLORS.red}22`, border: `${COLORS.red}55` };
  }
  if (status === 'yellow') {
    return {
      label: trend === 'green' ? 'Gelb nach Grün-Wechsel' : trend === 'red' ? 'Gelb nach Rot-Wechsel' : 'Gelb',
      color: COLORS.yellow,
      bg: `${COLORS.yellow}22`,
      border: `${COLORS.yellow}55`,
    };
  }
  return { label: 'Unbekannt', color: COLORS.muted, bg: '#ffffff08', border: COLORS.border };
}

function ChartWatermark({ themeMode }) {
  const watermarkLogo = themeMode === 'light' ? '/ET_App_Logo_Schild_green.png' : '/ET_App_Logo_Schild_white.png';
  const watermarkOpacity = themeMode === 'light' ? 0.07 : 0.08;
  return (
    <img
      src={watermarkLogo}
      alt=""
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: '230px',
        maxWidth: '24%',
        height: 'auto',
        transform: 'translate(-50%, -52%)',
        opacity: watermarkOpacity,
        pointerEvents: 'none',
        userSelect: 'none',
        zIndex: 0,
      }}
    />
  );
}

function LineChart({ item, COLORS, scale = 'absolute', displayMode = 'percent', themeMode = 'dark' }) {
  const width = 1160;
  const height = 360;
  const padding = { top: 18, right: 22, bottom: 42, left: 58 };
  const orange = COLORS.orange || '#f59e0b';
  const [hoverIndex, setHoverIndex] = useState(null);
  const points = useMemo(() => {
    const strategy = Array.isArray(item?.curve) ? item.curve : [];
    const benchmark = Array.isArray(item?.buy_hold_curve) ? item.buy_hold_curve : [];
    const leveraged = Array.isArray(item?.leveraged_curve) ? item.leveraged_curve : [];
    return { strategy, benchmark, leveraged };
  }, [item]);
  const timeFor = point => {
    const parsed = point?.date ? new Date(`${point.date}T00:00:00`).getTime() : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  };
  const allDatedPoints = [...points.strategy, ...points.benchmark, ...points.leveraged]
    .map(point => ({ point, time: timeFor(point) }))
    .filter(entry => entry.time !== null);
  const performanceFor = value => ((Number(value) / 10000) - 1) * 100;
  const displayValueFor = value => (displayMode === 'percent' ? performanceFor(value) : Number(value));
  const allValues = [...points.strategy, ...points.benchmark, ...points.leveraged]
    .map(p => Number(p.value))
    .filter(value => Number.isFinite(value) && value > 0);
  if (allValues.length < 2 || allDatedPoints.length < 2) {
    return <div style={{ color: COLORS.muted, fontSize: '13px' }}>Keine Chartdaten verfügbar.</div>;
  }
  const transformValue = value => (scale === 'log' ? Math.log(Math.max(Number(value), 0.000001)) : displayValueFor(value));
  const labelValueFromTransformed = value => (scale === 'log' ? displayValueFor(Math.exp(value)) : value);
  const transformedValues = allValues.map(transformValue);
  const min = Math.min(...transformedValues);
  const max = Math.max(...transformedValues);
  const span = max - min || 1;
  const minTime = Math.min(...allDatedPoints.map(entry => entry.time));
  const maxTime = Math.max(...allDatedPoints.map(entry => entry.time));
  const timeSpan = maxTime - minTime || 1;
  const xForDate = date => {
    const time = timeFor({ date });
    const ratio = time === null ? 0 : (time - minTime) / timeSpan;
    return padding.left + ratio * (width - padding.left - padding.right);
  };
  const yFor = value => padding.top + ((max - transformValue(value)) / span) * (height - padding.top - padding.bottom);
  const pathFor = series => series.map((point, index) => {
    const x = xForDate(point.date);
    const y = yFor(Number(point.value));
    return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  const grid = [0, 0.25, 0.5, 0.75, 1].map(ratio => {
    const transformed = max - span * ratio;
    const value = labelValueFromTransformed(transformed);
    const y = padding.top + ((max - transformed) / span) * (height - padding.top - padding.bottom);
    return { y, value };
  });
  const dateSource = [points.strategy, points.benchmark, points.leveraged].reduce(
    (longest, series) => (series.length > longest.length ? series : longest),
    [],
  );
  const yearTicks = [];
  const seenYears = new Set();
  const firstYear = Number(String(dateSource[0]?.date || '').slice(0, 4));
  const lastYear = Number(String(dateSource[dateSource.length - 1]?.date || '').slice(0, 4));
  const yearSpan = Number.isFinite(firstYear) && Number.isFinite(lastYear) ? Math.max(1, lastYear - firstYear) : 1;
  const yearStep = yearSpan > 24 ? 4 : yearSpan > 14 ? 2 : 1;
  dateSource.forEach((point) => {
    const year = Number(String(point.date || '').slice(0, 4));
    if (!Number.isFinite(year) || seenYears.has(year) || (year - firstYear) % yearStep !== 0) return;
    seenYears.add(year);
    yearTicks.push({ year, x: xForDate(point.date) });
  });
  const lastTickYear = Number(String(dateSource[dateSource.length - 1]?.date || '').slice(0, 4));
  if (Number.isFinite(lastTickYear) && !seenYears.has(lastTickYear)) {
    yearTicks.push({ year: lastTickYear, x: xForDate(dateSource[dateSource.length - 1]?.date) });
  }
  const nearestPoint = (series, targetTime) => {
    if (!series.length || targetTime === null) return null;
    return series.reduce((nearest, point) => {
      const pointTime = timeFor(point);
      if (pointTime === null) return nearest;
      if (!nearest) return point;
      const nearestTime = timeFor(nearest);
      return Math.abs(pointTime - targetTime) < Math.abs(nearestTime - targetTime) ? point : nearest;
    }, null);
  };
  const handleMouseMove = event => {
    if (!dateSource.length) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * width;
    const chartLeft = padding.left;
    const chartRight = width - padding.right;
    const ratio = Math.min(1, Math.max(0, (x - chartLeft) / Math.max(1, chartRight - chartLeft)));
    const targetTime = minTime + ratio * timeSpan;
    const nearestIndex = dateSource.reduce((bestIndex, point, index) => {
      const pointTime = timeFor(point);
      const bestTime = timeFor(dateSource[bestIndex]);
      if (pointTime === null || bestTime === null) return bestIndex;
      return Math.abs(pointTime - targetTime) < Math.abs(bestTime - targetTime) ? index : bestIndex;
    }, 0);
    setHoverIndex(nearestIndex);
  };
  const hoverPoint = Number.isInteger(hoverIndex) ? dateSource[hoverIndex] : null;
  const hoverX = hoverPoint ? xForDate(hoverPoint.date) : null;
  const hoverTime = timeFor(hoverPoint);
  const tooltipRows = [
    { label: 'Benchmark: Buy & Hold', color: COLORS.cyan, point: nearestPoint(points.benchmark, hoverTime) },
    { label: item?.strategy_name || 'Index-Invest', color: COLORS.green, point: nearestPoint(points.strategy, hoverTime) },
    { label: item?.leveraged_label || 'Index-Invest 2x Hebel', color: orange, point: nearestPoint(points.leveraged, hoverTime) },
  ].filter(row => row.point && Number.isFinite(Number(row.point.value)));
  const tooltipWidth = 210;
  const tooltipHeight = 30 + tooltipRows.length * 22;
  const tooltipX = hoverX !== null && hoverX > width - padding.right - tooltipWidth - 12 ? hoverX - tooltipWidth - 12 : (hoverX || padding.left) + 12;
  const tooltipY = padding.top + 10;
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <ChartWatermark themeMode={themeMode} />
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${item.label} Index-Invest Chart`}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverIndex(null)}
        style={{ width: '100%', height: 'auto', display: 'block', position: 'relative', zIndex: 1, cursor: 'crosshair' }}
      >
      <rect x="0" y="0" width={width} height={height} fill="transparent" />
      {grid.map(line => (
        <g key={line.y}>
          <line x1={padding.left} x2={width - padding.right} y1={line.y} y2={line.y} stroke={COLORS.border} strokeWidth="1" />
          <text x={padding.left - 8} y={line.y + 4} fill={COLORS.muted} fontSize="10" textAnchor="end">
            {displayMode === 'percent' ? formatPercent(line.value, 0) : formatNumber(line.value, 0)}
          </text>
        </g>
      ))}
      {yearTicks.map(tick => (
        <g key={tick.year}>
          <line x1={tick.x} x2={tick.x} y1={padding.top} y2={height - padding.bottom} stroke={COLORS.border} strokeWidth="1" opacity="0.5" />
          <text x={tick.x} y={height - 16} fill={COLORS.muted} fontSize="10" textAnchor="middle">
            {tick.year}
          </text>
        </g>
      ))}
      <path d={pathFor(points.benchmark)} fill="none" stroke={COLORS.cyan} strokeWidth="2" opacity="0.85" />
      <path d={pathFor(points.strategy)} fill="none" stroke={COLORS.green} strokeWidth="2.5" />
      <path d={pathFor(points.leveraged)} fill="none" stroke={orange} strokeWidth="2" opacity="0.9" />
      {hoverPoint && hoverX !== null && (
        <g pointerEvents="none">
          <line x1={hoverX} x2={hoverX} y1={padding.top} y2={height - padding.bottom} stroke={COLORS.text} strokeWidth="1" opacity="0.25" />
          {tooltipRows.map(row => (
            <circle
              key={row.label}
              cx={xForDate(row.point.date)}
              cy={yFor(Number(row.point.value))}
              r="4"
              fill={row.color}
              stroke={COLORS.card}
              strokeWidth="2"
            />
          ))}
          <rect
            x={tooltipX}
            y={tooltipY}
            width={tooltipWidth}
            height={tooltipHeight}
            rx="10"
            fill={COLORS.card}
            stroke={COLORS.border}
            opacity="0.96"
          />
          <text x={tooltipX + 12} y={tooltipY + 20} fill={COLORS.text} fontSize="12" fontWeight="800">
            {formatDate(hoverPoint.date)}
          </text>
          {tooltipRows.map((row, index) => (
            <g key={row.label}>
              <circle cx={tooltipX + 14} cy={tooltipY + 42 + index * 22} r="4" fill={row.color} />
              <text x={tooltipX + 24} y={tooltipY + 46 + index * 22} fill={COLORS.text} fontSize="11">
                {row.label}: {displayMode === 'percent' ? formatPercent(performanceFor(row.point.value), 0) : `${formatNumber(row.point.value, 0)} (${formatPercent(performanceFor(row.point.value), 0)})`}
              </text>
            </g>
          ))}
        </g>
      )}
      </svg>
    </div>
  );
}

export default function IndexInvestSection({ COLORS, onSessionExpired, themeMode = 'dark', canExportCsv = false }) {
  const [payload, setPayload] = useState(null);
  const [selectedKey, setSelectedKey] = useState('sp500');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [chartScale, setChartScale] = useState('log');
  const [chartDisplayMode, setChartDisplayMode] = useState('percent');
  const [cashInterestAnnualPct, setCashInterestAnnualPct] = useState('0');
  const [tradeFee, setTradeFee] = useState('0');
  const [annualTaxPct, setAnnualTaxPct] = useState('0');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  function buildAssumptionQuery() {
    const params = new URLSearchParams({
      cash_interest_annual_pct: String(Number(cashInterestAnnualPct) || 0),
      trade_fee: String(Number(tradeFee) || 0),
      annual_tax_pct: String(Number(annualTaxPct) || 0),
    });
    return params.toString();
  }

  async function loadIndexInvest() {
    setLoading(true);
    setError('');
    try {
      const data = await apiRequest(`/api/index-invest?${buildAssumptionQuery()}`);
      setPayload(data);
      const firstKey = data?.indices?.[0]?.key;
      if (firstKey && !data.indices.some(item => item.key === selectedKey)) {
        setSelectedKey(firstKey);
      }
    } catch (err) {
      if (err?.status === 401 && onSessionExpired) onSessionExpired();
      setError(err?.message || 'Index-Invest konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }

  async function saveEmailEnabled(nextValue) {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const data = await apiRequest('/api/index-invest/settings', {
        method: 'PATCH',
        body: { email_enabled: nextValue },
      });
      setPayload(prev => ({ ...(prev || {}), email_enabled: !!data.email_enabled }));
      setMessage('Benachrichtigung gespeichert');
    } catch (err) {
      if (err?.status === 401 && onSessionExpired) onSessionExpired();
      setError(err?.message || 'Einstellung konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    loadIndexInvest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const indices = Array.isArray(payload?.indices) ? payload.indices : [];
  const selected = indices.find(item => item.key === selectedKey) || indices[0] || null;
  const meta = selected ? statusMeta(selected.status, selected.trend, COLORS) : null;
  const strategyName = selected?.strategy_name || 'Index-Invest';
  const indexDisplay = {
    sp500: { label: 'S&P 500', icon: '🇺🇸' },
    nasdaq100: { label: 'Nasdaq 100', icon: '🇺🇸' },
    dax: { label: 'DAX', icon: '🇩🇪' },
    msci_world: { label: 'MSCI World', icon: '🌍' },
  };

  function exportSelectedIndexCsv() {
    if (!selected || !canExportCsv) return;
    const strategyCurve = Array.isArray(selected.curve) ? selected.curve : [];
    const buyHoldCurve = Array.isArray(selected.buy_hold_curve) ? selected.buy_hold_curve : [];
    const leveragedCurve = Array.isArray(selected.leveraged_curve) ? selected.leveraged_curve : [];
    const byDate = new Map();
    const ensureRow = date => {
      if (!byDate.has(date)) {
        byDate.set(date, { date, strategy: '', buyHold: '', leveraged: '' });
      }
      return byDate.get(date);
    };
    strategyCurve.forEach(point => {
      ensureRow(point.date).strategy = formatCsvNumber(point.value, 2);
    });
    buyHoldCurve.forEach(point => {
      ensureRow(point.date).buyHold = formatCsvNumber(point.value, 2);
    });
    leveragedCurve.forEach(point => {
      ensureRow(point.date).leveraged = formatCsvNumber(point.value, 2);
    });

    const rows = [
      toCsvRow(['Index-Invest Export']),
      toCsvRow(['Index', selected.label || '']),
      toCsvRow(['Stand', selected.as_of || '']),
      toCsvRow(['Signal', statusMeta(selected.status, selected.trend, COLORS).label]),
      toCsvRow(['Signal seit', selected.signal_date || '']),
      toCsvRow(['Strategie', strategyName]),
      toCsvRow(['2x-Produkt-Beispiel', selected.leveraged_product_example || (selected.leveraged_symbol ? `${selected.leveraged_symbol} ${selected.leveraged_name || ''}` : '')]),
      toCsvRow(['2x-Hinweis', selected.leveraged_proxy_note || '']),
      toCsvRow(['ATR Länge', selected.length || '']),
      toCsvRow(['Multiplikator', selected.multiplier || '']),
      '',
      toCsvRow(['Kennzahl', 'Benchmark: Buy & Hold', strategyName, selected.leveraged_label || 'Index-Invest 2x Hebel']),
      toCsvRow(['Endwert', formatCsvNumber(selected.buy_hold_value, 0), formatCsvNumber(selected.strategy_value, 0), formatCsvNumber(selected.leveraged_value, 0)]),
      toCsvRow(['CAGR %', formatCsvNumber(selected.buy_hold_cagr, 0), formatCsvNumber(selected.strategy_cagr, 0), formatCsvNumber(selected.leveraged_cagr, 0)]),
      toCsvRow(['Max Drawdown %', formatCsvNumber(selected.buy_hold_max_dd, 0), formatCsvNumber(selected.strategy_max_dd, 0), formatCsvNumber(selected.leveraged_max_dd, 0)]),
      toCsvRow(['Trades', '', selected.simulation_trades ?? '', selected.leveraged_trades ?? '']),
      toCsvRow(['Gebühren', '', formatCsvNumber(selected.simulation_fees, 0), formatCsvNumber(selected.leveraged_fees, 0)]),
      toCsvRow(['Steuern', '', formatCsvNumber(selected.simulation_taxes, 0), formatCsvNumber(selected.leveraged_taxes, 0)]),
      '',
      toCsvRow(['Datum', 'Benchmark: Buy & Hold', strategyName, selected.leveraged_label || 'Index-Invest 2x Hebel']),
      ...Array.from(byDate.values())
        .sort((left, right) => String(left.date).localeCompare(String(right.date)))
        .map(row => toCsvRow([row.date, row.buyHold, row.strategy, row.leveraged])),
    ];

    const blob = new Blob(['\uFEFF', rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `index-invest-${csvFilenamePart(selected.key || selected.label)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  return (
    <div>
      <style>{`
        @keyframes index-invest-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      {guideOpen && (
        <IndexInvestGuideModal
          COLORS={COLORS}
          onClose={() => setGuideOpen(false)}
        />
      )}
      <div style={{ marginBottom: '18px' }}>
        <div>
          <h1 style={{ margin: '0 0 8px', fontSize: '26px' }}>Index-Invest</h1>
        </div>
      </div>

      <div style={{ display: 'grid', gap: '18px', alignItems: 'start' }}>
        <section style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: '16px', padding: '18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {indices.map(item => {
                const display = indexDisplay[item.key] || { label: item.label, icon: '' };
                const active = selected?.key === item.key;
                return (
                  <button
                    key={item.key}
                    onClick={() => setSelectedKey(item.key)}
                    style={{
                      border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
                      background: active ? COLORS.accent : '#ffffff08',
                      color: active ? '#fff' : COLORS.muted,
                      borderRadius: '10px',
                      padding: '10px 14px',
                      fontSize: '13px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      minHeight: '40px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '7px',
                    }}
                  >
                    <span aria-hidden="true">{display.icon}</span>
                    <span>{display.label}</span>
                  </button>
                );
              })}
            </div>
            <label style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '9px',
              border: `1px solid ${COLORS.border}`,
              background: '#ffffff06',
              color: COLORS.text,
              borderRadius: '10px',
              padding: '10px 12px',
              minHeight: '40px',
              boxSizing: 'border-box',
              fontSize: '13px',
              fontWeight: 800,
              cursor: saving ? 'default' : 'pointer',
              marginLeft: 'auto',
            }}>
              <input
                type="checkbox"
                checked={!!payload?.email_enabled}
                disabled={saving}
                onChange={event => saveEmailEnabled(event.target.checked)}
                style={{ width: '16px', height: '16px' }}
              />
              <span>Email Alert</span>
            </label>
            <button
              type="button"
              onClick={() => setGuideOpen(true)}
              style={{
                border: `1px solid ${COLORS.border}`,
                background: '#ffffff08',
                color: COLORS.text,
                borderRadius: '10px',
                padding: '10px 12px',
                minHeight: '40px',
                boxSizing: 'border-box',
                fontSize: '13px',
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              Anleitung
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', marginBottom: '16px' }}>
            <AssumptionInput
              label="Cash-Zins p.a. %"
              value={cashInterestAnnualPct}
              onChange={setCashInterestAnnualPct}
              COLORS={COLORS}
              min="-20"
              max="50"
              step="0.1"
            />
            <AssumptionInput
              label="Gebühr je Trade"
              value={tradeFee}
              onChange={setTradeFee}
              COLORS={COLORS}
              min="0"
              step="0.01"
            />
            <AssumptionInput
              label="Steuersatz auf Gewinne %"
              value={annualTaxPct}
              onChange={setAnnualTaxPct}
              COLORS={COLORS}
              min="0"
              max="100"
              step="0.1"
            />
            <button
              type="button"
              onClick={loadIndexInvest}
              disabled={loading}
              style={{
                alignSelf: 'end',
                height: '42px',
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                borderRadius: '10px',
                padding: '9px 16px',
                fontSize: '13px',
                fontWeight: 700,
                boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
                cursor: loading ? 'default' : 'pointer',
                transition: 'filter 120ms ease',
              }}
            >
              {loading ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                  <span
                    aria-hidden="true"
                    style={{
                      width: '13px',
                      height: '13px',
                      border: '2px solid rgba(255,255,255,0.45)',
                      borderTopColor: '#ffffff',
                      borderRadius: '50%',
                      animation: 'index-invest-spin 0.8s linear infinite',
                    }}
                  />
                  Berechne ...
                </span>
              ) : 'Annahmen anwenden'}
            </button>
          </div>

          {error && <div style={{ color: COLORS.red, marginBottom: '12px', fontSize: '13px' }}>⚠ {error}</div>}
          {message && <div style={{ color: COLORS.green, marginBottom: '12px', fontSize: '13px' }}>{message}</div>}
          {!selected && !loading && <div style={{ color: COLORS.muted }}>Keine Indexdaten verfügbar.</div>}
          {selected && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', marginBottom: '16px' }}>
                <div style={{ border: `1px solid ${meta.border}`, background: meta.bg, borderRadius: '12px', padding: '12px' }}>
                  <div style={{ color: COLORS.muted, fontSize: '11px', textTransform: 'uppercase', fontWeight: 800 }}>Status Index-Invest</div>
                  <div style={{ color: meta.color, fontSize: '18px', fontWeight: 900 }}>{meta.label}</div>
                </div>
                <Metric label="Signal seit" value={formatDate(selected.signal_date)} COLORS={COLORS} />
                <Metric label="Stand" value={formatDate(selected.as_of)} COLORS={COLORS} />
                <Metric label="Zeit im Markt" value={formatPercent(selected.time_in_market_pct, 0)} COLORS={COLORS} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <div style={{ display: 'inline-flex', background: '#ffffff08', border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflow: 'hidden' }}>
                  {[
                    ['percent', 'Prozent'],
                    ['value', 'Wert'],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setChartDisplayMode(value)}
                      style={{
                        border: 'none',
                        borderRight: value === 'percent' ? `1px solid ${COLORS.border}` : 'none',
                        background: chartDisplayMode === value ? COLORS.cyan : 'transparent',
                        color: chartDisplayMode === value ? '#00131a' : COLORS.text,
                        padding: '7px 10px',
                        fontSize: '12px',
                        fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'inline-flex', background: '#ffffff08', border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflow: 'hidden' }}>
                  {[
                    ['absolute', 'Absolut'],
                    ['log', 'Logarithmisch'],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setChartScale(value)}
                      style={{
                        border: 'none',
                        borderRight: value === 'absolute' ? `1px solid ${COLORS.border}` : 'none',
                        background: chartScale === value ? COLORS.cyan : 'transparent',
                        color: chartScale === value ? '#00131a' : COLORS.text,
                        padding: '7px 10px',
                        fontSize: '12px',
                        fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <LineChart item={selected} COLORS={COLORS} scale={chartScale} displayMode={chartDisplayMode} themeMode={themeMode} />
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '10px', color: COLORS.muted, fontSize: '12px' }}>
                <span><span style={{ color: COLORS.cyan, fontWeight: 900 }}>━</span> Benchmark: Buy & Hold</span>
                <span><span style={{ color: COLORS.green, fontWeight: 900 }}>━</span> {strategyName}</span>
                <span><span style={{ color: COLORS.orange || '#f59e0b', fontWeight: 900 }}>━</span> {selected.leveraged_label || 'Index-Invest 2x Hebel'}</span>
                <span>Darstellung: {chartDisplayMode === 'percent' ? 'Prozent' : 'Wert'} · {chartScale === 'log' ? 'logarithmisch' : 'absolut'}</span>
              </div>
            </>
          )}
        </section>

        <div>
          {selected && (
            <section style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: '16px', padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
                <h2 style={{ margin: 0, fontSize: '16px' }}>Kennzahlen</h2>
                {canExportCsv && (
                  <button
                    type="button"
                    onClick={exportSelectedIndexCsv}
                    style={{
                      background: '#16a34a',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '6px 11px',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
                    }}
                  >
                    CSV Export
                  </button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px', marginBottom: '12px' }}>
                <ResultCard
                  title="Benchmark: Buy & Hold"
                  color={COLORS.cyan}
                  endValue={selected.buy_hold_value}
                  cagr={selected.buy_hold_cagr}
                  maxDd={selected.buy_hold_max_dd}
                  details={[
                    ['Startdatum', formatDate(selected.curve?.[0]?.date || selected.buy_hold_curve?.[0]?.date)],
                    ['Signalwechsel', selected.signal_changes ?? '–'],
                    ['VStop', formatNumber(selected.vstop, 0)],
                    ['ATR', formatNumber(selected.atr, 0)],
                  ]}
                  COLORS={COLORS}
                />
                <ResultCard
                  title={strategyName}
                  color={COLORS.green}
                  endValue={selected.strategy_value}
                  cagr={selected.strategy_cagr}
                  maxDd={selected.strategy_max_dd}
                  details={[
                    ['Startdatum', formatDate(selected.curve?.[0]?.date)],
                    ['Trades', selected.simulation_trades ?? '–'],
                    ['Gebühren', formatNumber(selected.simulation_fees, 0)],
                    ['Steuern', formatNumber(selected.simulation_taxes, 0)],
                  ]}
                  COLORS={COLORS}
                />
                <ResultCard
                  title={selected.leveraged_label || 'Index-Invest 2x Hebel'}
                  color={COLORS.orange || '#f59e0b'}
                  endValue={selected.leveraged_value}
                  cagr={selected.leveraged_cagr}
                  maxDd={selected.leveraged_max_dd}
                  details={[
                    ['Startdatum', formatDate(selected.leveraged_start_date)],
                    ['Trades', selected.leveraged_trades ?? '–'],
                    ['Gebühren', formatNumber(selected.leveraged_fees, 0)],
                    ['Steuern', formatNumber(selected.leveraged_taxes, 0)],
                    ['Produkt-Beispiel', selected.leveraged_product_example || (selected.leveraged_symbol ? `${selected.leveraged_symbol} · ${selected.leveraged_name || ''}` : '–')],
                    ...(selected.leveraged_proxy_note ? [['Hinweis', selected.leveraged_proxy_note]] : []),
                  ]}
                  COLORS={COLORS}
                />
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function IndexInvestGuideModal({ COLORS, onClose }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="index-invest-guide-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0, 8, 12, 0.72)',
        display: 'grid',
        placeItems: 'center',
        padding: '22px',
      }}
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: 'min(780px, 100%)',
          maxHeight: 'min(82vh, 760px)',
          overflow: 'auto',
          background: COLORS.card,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '16px',
          boxShadow: '0 24px 80px rgba(0,0,0,0.42)',
        }}
      >
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          padding: '16px 18px',
          background: COLORS.card,
          borderBottom: `1px solid ${COLORS.border}`,
        }}>
          <div>
            <h2 id="index-invest-guide-title" style={{ margin: 0, color: COLORS.text, fontSize: '20px' }}>Index-Invest Anleitung</h2>
            <div style={{ color: COLORS.muted, fontSize: '13px', marginTop: '4px' }}>Regelbasiertes Steuerungssystem für langfristige ETF-Portfolios.</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Anleitung schließen"
            style={{
              border: `1px solid ${COLORS.border}`,
              background: '#ffffff08',
              color: COLORS.text,
              borderRadius: '10px',
              width: '38px',
              height: '38px',
              fontSize: '20px',
              fontWeight: 800,
              cursor: 'pointer',
              flex: '0 0 auto',
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: '18px', display: 'grid', gap: '14px' }}>
          {INDEX_INVEST_GUIDE_SECTIONS.map(section => (
            <section
              key={section.title}
              style={{
                border: `1px solid ${COLORS.border}`,
                background: '#ffffff05',
                borderRadius: '12px',
                padding: '15px',
              }}
            >
              <h3 style={{ margin: '0 0 10px', color: COLORS.text, fontSize: '16px' }}>{section.title}</h3>
              {section.paragraphs?.map(paragraph => (
                <p key={paragraph} style={{ margin: '0 0 10px', color: COLORS.muted, fontSize: '14px', lineHeight: 1.65 }}>
                  {paragraph}
                </p>
              ))}
              {section.bullets && (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {section.bullets.map(bullet => (
                    <div key={bullet} style={{ color: COLORS.muted, fontSize: '14px', lineHeight: 1.55 }}>
                      {bullet}
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, COLORS }) {
  return (
    <div style={{ border: `1px solid ${COLORS.border}`, background: '#ffffff06', borderRadius: '12px', padding: '12px' }}>
      <div style={{ color: COLORS.muted, fontSize: '11px', textTransform: 'uppercase', fontWeight: 800 }}>{label}</div>
      <div style={{ color: COLORS.text, fontSize: '15px', fontWeight: 900 }}>{value}</div>
    </div>
  );
}

function AssumptionInput({ label, value, onChange, COLORS, min, max, step }) {
  return (
    <label style={{ display: 'grid', gap: '6px', color: COLORS.muted, fontSize: '12px', fontWeight: 800 }}>
      {label}
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={event => onChange(event.target.value)}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          border: `1px solid ${COLORS.border}`,
          borderRadius: '10px',
          background: '#ffffff08',
          color: COLORS.text,
          padding: '10px 12px',
          fontSize: '14px',
          fontWeight: 800,
        }}
      />
    </label>
  );
}

function ResultCard({ title, color, endValue, cagr, maxDd, details = [], COLORS }) {
  const isNegativeDrawdown = Number(maxDd) < 0;
  const totalReturn = ((Number(endValue) / 10000) - 1) * 100;
  return (
    <div style={{
      border: `1px solid ${COLORS.border}`,
      background: `linear-gradient(180deg, ${color}12 0%, #ffffff05 46%, #ffffff04 100%)`,
      borderRadius: '12px',
      padding: '14px',
      minHeight: '196px',
      display: 'grid',
      alignContent: 'start',
      gap: '12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', minHeight: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: color, flex: '0 0 auto', boxShadow: `0 0 0 4px ${color}18` }} />
          <strong style={{ color: COLORS.text, fontSize: '12px', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</strong>
        </div>
        <span style={{ color, fontSize: '11px', fontWeight: 900, textTransform: 'uppercase' }}>Endwert</span>
      </div>
      <div style={{ display: 'grid', gap: '3px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ color: COLORS.text, fontSize: '24px', lineHeight: 1, fontWeight: 950 }}>{formatNumber(endValue, 0)}</span>
          <span style={{ color: Number(totalReturn) >= 0 ? COLORS.green : COLORS.red, fontSize: '13px', fontWeight: 900 }}>
            ({Number(totalReturn) >= 0 ? '+' : ''}{formatPercent(totalReturn, 0)})
          </span>
        </div>
        <div style={{ color: COLORS.muted, fontSize: '11px' }}>Startwert 10.000</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <div style={{ border: `1px solid ${COLORS.border}`, background: '#00000018', borderRadius: '10px', padding: '8px' }}>
          <div style={{ color: COLORS.muted, fontSize: '10px', textTransform: 'uppercase', fontWeight: 800, marginBottom: '3px' }}>CAGR</div>
          <strong style={{ color: Number(cagr) >= 0 ? COLORS.green : COLORS.red, fontSize: '14px' }}>{formatPercent(cagr, 0)}</strong>
        </div>
        <div style={{ border: `1px solid ${COLORS.border}`, background: '#00000018', borderRadius: '10px', padding: '8px' }}>
          <div style={{ color: COLORS.muted, fontSize: '10px', textTransform: 'uppercase', fontWeight: 800, marginBottom: '3px' }}>Max DD</div>
          <strong style={{ color: isNegativeDrawdown ? COLORS.red : COLORS.text, fontSize: '14px' }}>{formatPercent(maxDd, 0)}</strong>
        </div>
      </div>
      {details.length > 0 && (
        <div style={{ display: 'grid', gap: '7px', paddingTop: '2px' }}>
          {details.map(([label, value]) => {
            const longDetail = label === 'Produkt-Beispiel' || label === 'Hinweis';
            return (
              <div key={label} style={{
                display: 'grid',
                gridTemplateColumns: longDetail ? '1fr' : '110px minmax(0, 1fr)',
                gap: longDetail ? '3px' : '8px',
                alignItems: 'baseline',
                fontSize: '11px',
                lineHeight: 1.35,
                minHeight: '16px',
              }}>
                <span style={{ color: COLORS.muted }}>{label}</span>
                <strong
                  style={{
                    color: COLORS.text,
                    textAlign: longDetail ? 'left' : 'right',
                    overflow: longDetail ? 'visible' : 'hidden',
                    textOverflow: longDetail ? 'clip' : 'ellipsis',
                    whiteSpace: longDetail ? 'normal' : 'nowrap',
                  }}
                  title={String(value ?? '')}
                >
                  {value}
                </strong>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
